/**
 * Imperial Administrator (IADMIN) deep-link builder — single source of truth
 * dla URL'i admin panelu generowanych w React-CMS.
 *
 * **Dwie kategorie linków:**
 *
 * 1. **Static** — generowane lokalnie przez factory functions (`iadminUrl.*`).
 *    Używamy dla NAWIGACYJNYCH / GLOBALNYCH akcji (np. "Dodaj katalog",
 *    "Dodaj okno", system admin pages). Frontend zna wszystkie te URL'e
 *    statycznie — backend nie ma value-add (nie ma conditional logic,
 *    tylko constant template).
 *
 * 2. **Dynamic** — zwracane przez backend w response payload (np.
 *    `WindowAdminLinks.config_url` per okno). Backend dodaje conditional
 *    logic (np. `content_url=null` gdy komponent okna nie ma pliku
 *    `_admin.php`). React-CMS bierze gotowy query string z response i
 *    przekazuje do `buildIadminFullUrl`.
 *
 * **Wspólny composer:** `buildIadminFullUrl(adminPanelUrl, qs, options?)`
 * prependuje host + opcjonalnie dokleja `continue=<current URL>` (dla
 * scenariuszy gdzie IADMIN ma wrócić do React-CMS po zakończeniu).
 *
 * **Continue param** wymaga `window.location.href` -> tylko client-side.
 * SSR-safe: gdy `window` undefined, zwraca URL bez continue (browser
 * dokleje na hydration nie obchodzi — używamy WYŁĄCZNIE dla `target="_blank"`
 * scenariuszy, gdzie nowa karta nie wraca do oryginalnej).
 *
 * **Wzorzec parametrów `sys` / `adminPage`:**
 * - `?admin_page=X` — top-level page (catalogs, windows_manager, editwindow)
 * - `?admin_page=system_administration&sys=Y` — sub-page w sekcji System administracyjny
 *   (windows_manager, window_containers, domains, documentation, ...)
 *
 * **Hardcoded `s_lang=pl`** — admin UI language. Backend `buildAdminLinksForWindow`
 * używa tej samej konwencji (TODO: per-user preference). `sa_lang` (managed
 * content lang) pochodzi z React-CMS locale.
 *
 * @example Static URL — Add catalog
 * const href = buildIadminFullUrl(adminPanelUrl, iadminUrl.addCatalog({ locale }));
 *
 * @example Dynamic URL z backendu (per-window config link)
 * const href = buildIadminFullUrl(adminPanelUrl, window.adminLinks.configUrl, { withContinue: true });
 *
 * @example System admin section z params
 * const href = buildIadminFullUrl(
 *   adminPanelUrl,
 *   iadminUrl.systemSys({ sys: "domains", params: { action: "templates", id: 5 } }),
 * );
 */

/**
 * Buduje query string ze słownika parametrów. Encoduje wartości i klucze
 * przez `URLSearchParams` — bezpieczny dla special chars (`&`, `=`, `?` etc.).
 * Zwraca string bez wiodącego `?` (caller dokleja `&` lub `?`).
 */
function buildParamsString(params: Record<string, string | number>): string {
  return new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)]),
  ).toString();
}

/**
 * Static URL factory — generuje query strings do typowych admin pages.
 *
 * Każda metoda zwraca query string z wiodącym `?` (composer dokleja
 * adminPanelUrl bez separator'a). Frontend nigdy nie musi pamiętać
 * dokładnych nazw `admin_page` / `sys` — typed API zapobiega literówkom.
 */
export const iadminUrl = {
  /**
   * Dodaj nowy katalog (Imperial Administrator > Katalogi > formularz).
   */
  addCatalog: (opts: { locale: string }): string =>
    `?s_lang=pl&sa_lang=${encodeURIComponent(opts.locale)}&admin_page=catalogs&action=add`,

  /**
   * Dodaj podstronę jako dziecko aktualnego katalogu.
   * Backend: `id_parent` ustawia parent w hierarchii `imp_cms_catalog`.
   */
  addCatalogChild: (opts: { locale: string; idParent: number }): string =>
    `?s_lang=pl&sa_lang=${encodeURIComponent(opts.locale)}&admin_page=catalogs&action=add&id_parent=${opts.idParent}`,

  /**
   * Edycja konfiguracji katalogu (nazwa, symbol, layout, metatags, parent, etc.).
   * Formularz w `ADMIN/pages/catalogs/catalogs.php` action=edit.
   */
  editCatalog: (opts: { locale: string; id: number }): string =>
    `?s_lang=pl&sa_lang=${encodeURIComponent(opts.locale)}&admin_page=catalogs&action=edit&id=${opts.id}`,

  /**
   * Zarządzanie treścią katalogu — dispatcher do komponentu `c_*_admin.php`
   * przez stronę `editcatalog`. Tu admin edytuje wpisy/produkty/artykuły
   * w katalogu (zależnie od typu komponentu CMS przypisanego do katalogu).
   */
  editCatalogContent: (opts: { locale: string; idCatalog: number }): string =>
    `?s_lang=pl&sa_lang=${encodeURIComponent(opts.locale)}&admin_page=editcatalog&id_catalog=${opts.idCatalog}`,

  /**
   * Usuwanie katalogu (Imperial Administrator > Katalogi > delete action).
   * UWAGA: backend nie ma confirmDialog'a — całość usuwana po pierwszym kliknięciu.
   * React-CMS powinien pokazać własne potwierdzenie PRZED otwarciem tego URL'a.
   */
  deleteCatalog: (opts: { locale: string; id: number }): string =>
    `?s_lang=pl&sa_lang=${encodeURIComponent(opts.locale)}&admin_page=catalogs&action=delete&id=${opts.id}`,

  /**
   * Dodaj nowe okno (Imperial Administrator > Windows Manager > formularz).
   */
  addWindow: (opts: { locale: string }): string =>
    `?s_lang=pl&sa_lang=${encodeURIComponent(opts.locale)}&admin_page=windows_manager&action=add`,

  /**
   * Otwórz top-level admin page (np. `catalogs`, `windows_manager`, `editwindow`)
   * z opcjonalnymi parametrami (action, id, ...).
   */
  adminPage: (opts: {
    page: string;
    params?: Record<string, string | number>;
  }): string => {
    const base = `?admin_page=${encodeURIComponent(opts.page)}`;
    if (!opts.params) return base;
    return `${base}&${buildParamsString(opts.params)}`;
  },

  /**
   * Otwórz subpage w sekcji "System administracyjny" (sys=X).
   * Najczęstsze użycie: dev banner deep-linki do "domains", "documentation",
   * "window_containers" itd.
   */
  systemSys: (opts: {
    sys: string;
    params?: Record<string, string | number>;
  }): string => {
    const base = `?admin_page=system_administration&sys=${encodeURIComponent(opts.sys)}`;
    if (!opts.params) return base;
    return `${base}&${buildParamsString(opts.params)}`;
  },

  /**
   * Otwórz dokumentację React-CMS (lub inny doc symbol).
   * Skrót dla `systemSys({ sys: "documentation", params: { doc } })`.
   */
  documentation: (opts: { doc: string }): string =>
    `?admin_page=system_administration&sys=documentation&doc=${encodeURIComponent(opts.doc)}`,
} as const;

/**
 * Łączy `adminPanelUrl` (host + `/IADMIN`) z query string do pełnego URL'a.
 *
 * Działa identycznie dla:
 * - **Static qs** z `iadminUrl.*` helpers
 * - **Dynamic qs** z backendu (np. `WindowAdminLinks.config_url` z
 *   `IAPI_unpublishedWindows`)
 *
 * @param adminPanelUrl Host + `/IADMIN` z `useAdmin().adminPanelUrl`
 * @param queryString Query string z wiodącym `?` lub bez (auto-detect)
 * @param options.withContinue Dokleja `&continue=<current URL>` żeby IADMIN
 *   wrócił do React-CMS po zakończeniu. UWAGA: powoduje hydration mismatch
 *   gdy używane w SSR-renderowanym `<a href>`. Używaj WYŁĄCZNIE w klikalnych
 *   linkach gdzie continue ma sens (NIE dla `target="_blank"`, bo nowa karta
 *   nie wraca do original tab po close).
 */
export function buildIadminFullUrl(
  adminPanelUrl: string,
  queryString: string,
  options?: { withContinue?: boolean },
): string {
  /* Normalize queryString — może mieć `?` na początku lub być raw `key=val&...`. */
  const qs = queryString.startsWith("?") ? queryString : `?${queryString}`;
  const base = adminPanelUrl + qs;

  if (!options?.withContinue) return base;

  /* SSR-safe — bez window, return without continue (tak czy inaczej nie powinno
     się używać withContinue w SSR scope, ale defensive). */
  if (typeof window === "undefined") return base;

  const sep = qs.includes("?") ? "&" : "?";
  return `${base}${sep}continue=${encodeURIComponent(window.location.href)}`;
}
